<template>
	<div class="schedule-card">
		<div
			:class="['schedule-card__main', isCurrentSchedule && 'is-current']"
		>
			<div class="schedule-card__main-move">
				<div class="schedule-card__main-move-date">
					<span
						class="schedule-card__main-move-date-txt"
						v-if="isCurrentSchedule"
						>Moving on
					</span>
					{{ shortDateString(environment.scheduledDate) }}
				</div>
				<div
					class="schedule-card__main-move-hour"
					v-if="isCurrentSchedule"
				>
					6:30 AM EST
				</div>
			</div>
			<div class="schedule-card__main-actions">
				<ButtonGroup v-if="isCurrentSchedule">
					<template slot="container">
						<Button
							labelText="Edit Schedule"
							:btnClick="editSchedule"
							:disabled="!hasUpdatePermission"
						/>
						<Button
							className="btn--caution"
							labelText="Stop Schedule"
							:btnClick="stopSchedule"
						/>
					</template>
				</ButtonGroup>

				<span
					class="schedule-card__main-actions-reschedule"
					@mouseover="tooltipMoveToVisible = true"
					@mouseleave="tooltipMoveToVisible = false"
					v-if="hideRescheduleButton"
					ref="reschedule2Ref"
				>
					<Button
						id="rescheduleButton"
						labelText="Reschedule"
						:btnClick="reschedule"
						:disabled="disableRescheduleButton"
					/>

					<Tooltip
						:text="'There is a schedule already created.'"
						:display="displayRescheduleTooltip"
						:trackedRefName="'reschedule2Ref'"
						position="top-start"
						:offset="[0, 12]"
					/>
				</span>
			</div>
		</div>

		<div class="schedule-card__body">
			<div class="schedule-card__body-tags">
				<TagLabel
					v-if="!isCurrentSchedule"
					:message="environment.status"
					:state="statusTagState"
					ref="statusTagLabel"
				/>
				<TagLabel
					:message="moveToProdTagMessage"
					:state="'secondary-2'"
					ref="moveToProdTagLabel"
				/>
				<TagLabel
					:message="releaseTypeTagMessage"
					:state="'secondary-4'"
					ref="negativeTagLabel"
				/>
			</div>
			<div class="schedule-card__body-comment" v-if="showActionComment">
				{{ environment.actionComment }}
			</div>
			<div class="schedule-card__body-comment">
				{{ environment.comment }}
			</div>
			<div class="schedule-card__body-clients">
				{{ customersBySchedule }}
			</div>
		</div>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import ButtonGroup from '@/components/forms/button/button-group';
import TagLabel from '@/components/visual/tag-label';
import Tooltip from '@/components/shells/tooltip/tooltip';
import DatetimeMixin from '@/mixins/datetime-mixin';
//vue
import { mapState } from 'vuex';
//variable
import { CURRENT_SCHEDULE } from '@/sections/environment/environment-mutation-type.js';

import _ from 'lodash';

export default {
	name: 'EnvironmentScheduleCard',
	mixins: [DatetimeMixin],
	components: {
		Button,
		ButtonGroup,
		TagLabel,
		Tooltip
	},
	props: {
		environment: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			tooltipMoveToVisible: false
		};
	},
	computed: {
		...mapState('environments', [CURRENT_SCHEDULE]),
		...mapState('options', ['OptionsEnvironments']),

		isCurrentSchedule() {
			return (
				_.get(this.environment, 'id', false) ===
				_.get(this[CURRENT_SCHEDULE], 'id', false)
			);
		},
		customersBySchedule() {
			const customers = _.get(this.environment, 'customers', []);
			if (customers.length) {
				return customers
					.map(customer => customer.customerName)
					.join(', ');
			}
			return '';
		},
		hideRescheduleButton() {
			return (
				!this.isCurrentSchedule && this.environment.status !== 'SUCCESS'
			);
		},
		disableRescheduleButton() {
			return !_.isEmpty(this[CURRENT_SCHEDULE]);
		},
		displayRescheduleTooltip() {
			return this.tooltipMoveToVisible && this.disableRescheduleButton;
		},
		statusTagState() {
			const state = {
				SUCCESS: 'positive',
				ERROR: 'negative',
				STOPPED: 'warning'
			};
			return state[this.environment.status];
		},
		releaseTypeTagMessage() {
			const state = {
				H: 'HOTFIX',
				R: 'RELEASE'
			};
			return state[this.environment.releaseType];
		},
		moveToProdTagMessage() {
			if (!_.isEmpty(this['OptionsEnvironments'])) {
				const environment = this['OptionsEnvironments'].find(
					env => parseInt(env.value) === this.environment.moveToZoneId
				);
				if (!_.isEmpty(environment)) {
					return `MOVE TO ${environment.description.toUpperCase()}`;
				}
			}
			return '';
		},
		showActionComment() {
			return this.environment.status === 'STOPPED';
		},
		hasUpdatePermission() {
			return this.validatePermission('environments.update');
		},
	},
	methods: {
		editSchedule() {
			this.$router.push({
				path: `/environments/schedule/${this.environment.id}/edit`
			});
		},
		reschedule() {
			this.$router.push({
				path: `/environments/schedule/${this.environment.id}/copy`
			});
		},
		stopSchedule() {
			this.$emit('showStopModal', true);
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/scss/5-objects/_cards.scss';

/**
  * Schedule Card
  * Can use SCSS variables = TRUE
*/

.schedule-card {
	@include card('indicator');
	@include body-text('regular');

	&__main {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: $gutter-sm;
	}

	&__main-move-date {
		@include heading('md');
	}

	&__main-move-hour {
		text-transform: uppercase;
	}

	&__body {
		display: flex;
		flex-direction: column;
		gap: $gutter-lg;
	}

	&__body-tags {
		display: flex;
		gap: $gutter-md;
	}

	&__body-comment,
	&__body-clients {
		-webkit-hyphens: auto;
		-moz-hyphens: auto;
		-ms-hyphens: auto;
		hyphens: auto;
		word-break: break-all;
	}
}

/* MODIFIER FOR SPECIAL CARD MAIN STYLES*/
.is-current {
	width: calc(100% + 16px);
	padding: $card-padding;
	background-color: $neutral-4;
	border-bottom: solid 1px $neutral-2;
	margin-top: -$gutter-md;
	margin-left: -$gutter-md;
	margin-bottom: 10px;
}
</style>
